import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  IconButton, Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { authGet } from '../../services/apiService';

const NAVY  = '#12365a';
const RED   = '#b91c1c';
const AMBER = '#b45309';
const GREEN = '#059669';

const TIER_META = [
  { key: 'tier1', label: 'Tier 1 — Action Blocked',   color: RED,   bg: '#fef2f2', border: '#fecaca', chip: 'T1' },
  { key: 'tier2', label: 'Tier 2 — Adjustable Limit', color: AMBER, bg: '#fffbeb', border: '#fde68a', chip: 'T2' },
  { key: 'tier3', label: 'Tier 3 — UI Warning',        color: GREEN, bg: '#ecfdf5', border: '#a7f3d0', chip: 'T3' },
];

const HEADER_SX = {
  bgcolor: '#f6f8fb', color: '#6d788c', fontWeight: 700,
  fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.05em',
  whiteSpace: 'nowrap', py: 1, px: 1.5,
};

const fmt = n => (n == null ? '—' : n.toLocaleString());

export default function GuardrailCriteriaBreakdownTab({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [collapsed, setCollapsed] = useState({ tier1: false, tier2: false, tier3: false });

  const toggleCollapse = key =>
    setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    setLoading(true);
    authGet('/api/guardrail/criteria-breakdown', null, 'json', { useCache: false })
      .then(d => setData(d))
      .catch(err => setError(err?.message || 'Failed to load criteria breakdown'))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const allEmpty = TIER_META.every(t => (data?.breakdown?.[t.key] || []).length === 0);
  if (allEmpty) {
    return (
      <Alert severity="info">
        No tier thresholds configured yet. Set thresholds in the <strong>Tier Classification</strong> tab first.
      </Alert>
    );
  }

  const total = data.total || 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <Typography variant="body2" color="text.secondary">
          Each row shows how many clients breach that metric's threshold — clients can appear in multiple rows.
          Sorted by client count to surface the most impacted criteria first.
        </Typography>
      </Box>

      {TIER_META.map(({ key, label, color, bg, border, chip }) => {
        const rows = data?.breakdown?.[key] || [];
        const maxCount = rows[0]?.count || 1;

        return (
          <Paper
            key={key}
            elevation={0}
            sx={{ border: `1px solid ${border}`, borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(30,55,80,.07)' }}
          >
            <Box
              onClick={() => toggleCollapse(key)}
              sx={{ px: 2, py: 1.25, bgcolor: bg, borderBottom: collapsed[key] ? 'none' : `1px solid ${border}`, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', userSelect: 'none', '&:hover': { filter: 'brightness(0.97)' } }}
            >
              <Chip
                label={chip}
                size="small"
                sx={{ height: 22, fontSize: '0.72rem', fontWeight: 700, bgcolor: color, color: '#fff', border: 'none' }}
              />
              <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color }}>
                {label}
              </Typography>
              <Typography variant="caption" sx={{ color: '#6d788c', ml: 'auto' }}>
                {rows.length} criteria configured
              </Typography>
              <IconButton size="small" sx={{ ml: 0.5, color: '#6d788c', p: 0.25 }} onClick={e => { e.stopPropagation(); toggleCollapse(key); }}>
                <ExpandMoreIcon sx={{ fontSize: 20, transition: 'transform 0.25s', transform: collapsed[key] ? 'rotate(-90deg)' : 'rotate(0deg)' }} />
              </IconButton>
            </Box>

            <Collapse in={!collapsed[key]} timeout={220}>
              {rows.length === 0 ? (
                <Box px={2} py={2}>
                  <Typography variant="body2" color="text.secondary">No thresholds configured for this tier.</Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...HEADER_SX, width: '35%' }}>Metric</TableCell>
                        <TableCell sx={{ ...HEADER_SX, width: '15%' }} align="right">Threshold</TableCell>
                        <TableCell sx={{ ...HEADER_SX, width: '10%' }} align="right">Clients</TableCell>
                        <TableCell sx={{ ...HEADER_SX, width: '8%' }} align="right">% Total</TableCell>
                        <TableCell sx={{ ...HEADER_SX, width: '32%' }}>Breaching clients</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row, idx) => {
                        const barPct = maxCount > 0 ? (row.count / maxCount) * 100 : 0;
                        return (
                          <TableRow
                            key={row.field}
                            sx={{ bgcolor: idx % 2 === 0 ? '#fbfcfe' : 'transparent', '&:hover': { bgcolor: '#f1f7fb' } }}
                          >
                            <TableCell sx={{ fontSize: '0.82rem', color: NAVY, fontWeight: 500, px: 1.5, py: 0.9 }}>
                              {row.label}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.8rem', color: '#6d788c', px: 1.5, fontFamily: 'monospace' }}>
                              ≥ {fmt(row.threshold)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 700, color, px: 1.5, fontFamily: 'monospace' }}>
                              {fmt(row.count)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.8rem', color: '#6d788c', px: 1.5 }}>
                              {row.pct}%
                            </TableCell>
                            <TableCell sx={{ px: 1.5, py: 0.9 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flex: 1, height: 8, bgcolor: `${color}22`, borderRadius: 4, overflow: 'hidden' }}>
                                  <Box sx={{ width: `${barPct}%`, height: '100%', bgcolor: color, borderRadius: 4, transition: 'width 0.4s ease' }} />
                                </Box>
                                <Typography variant="caption" sx={{ color: '#9CA3AF', minWidth: 42, textAlign: 'right', fontSize: '0.7rem' }}>
                                  {fmt(total)} total
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Collapse>
          </Paper>
        );
      })}
    </Box>
  );
}
